import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { appendGuestSessionQuery } from "../utils/galleryInquiryUx";
import { adminApiKeyListPath, normalizeAdminApiKeyList } from "../utils/apiKeyProvisioning";

const API_BASE = process.env.REACT_APP_ROOM_NATE_API;
const API_VERSION = process.env.REACT_APP_ROOM_NATE_VERSION;
const baseUrl = `${(API_BASE ?? "").replace(/\/$/, "")}/${API_VERSION ?? ""}`;

const createRequest = (url) => ({ url: appendGuestSessionQuery(url) });

const createPostRequest = (url, data) => ({
  method: "POST",
  url,
  body: data,
});

const createDeleteRequest = (url) => ({
  method: "DELETE",
  url,
});

const createUpdateRequest = (url, data) => ({
  method: "PUT",
  url,
  body: data,
});

export const apiRoomNate = createApi({
  reducerPath: "apisRoomNate",
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const states = getState();
      headers.set(
        "Authorization",
        `Bearer ${
          states.roomNateSlice.currentToken
            ? states.roomNateSlice.currentToken.replace(/["]/g, "")
            : ""
        }`
      );
      return headers;
    },
  }),
  tagTypes: [
    "user",
    "items",
    "styles",
    "vibes",
    "amazonList",
    "externalUploadActivity",
    "savedLayouts",
    "apiKeys",
  ],
  endpoints: (builder) => ({
    getAllUsers: builder.query({
      query: (activePartner) => {
        const url = activePartner
          ? `/user/get-all?partner=${activePartner}`
          : `/user/get-all`;
        return createRequest(url);
      },
      providesTags: ["user"],
    }),    
    deleteUser: builder.mutation({
      query: (data) => createPostRequest(`/user/delete`, data),
      invalidatesTags: ["user"],
    }),
    createUser: builder.mutation({
      query: (data) => createPostRequest(`/user/signup`, data),
      invalidatesTags: ["user"],
    }),
    editUser: builder.mutation({
      query: ({ id, data }) => createPostRequest(`/user/edit/${id}`, data),
      invalidatesTags: ["user"],
    }),
    getAllItems: builder.query({
      query: ({ itemType, activePartner }) => {
        let url = `/items/type/${itemType}`;
        
        if ((Array.isArray(activePartner)  && activePartner?.length > 0 )|| (!Array.isArray(activePartner) &&  activePartner)) {
          const partnerParam = Array.isArray(activePartner)
            ? activePartner.join(',')
            : activePartner;
          url += `?partner=${partnerParam}`;
        }
    
        return createRequest(url);
      },
      providesTags: ["items"],
    }),    
    deleteItem: builder.mutation({
      query: (id) => createPostRequest(`/items/delete/${id}`),
      invalidatesTags: ["items"],
    }),
    createItems: builder.mutation({
      query: (data) => createPostRequest(`/items`, data),
      invalidatesTags: ["items"],
    }),
    updateItems: builder.mutation({
      query: ({ id, data }) => createPostRequest(`/items/update/${id}`, data),
      invalidatesTags: ["items"],
    }),
    getStyles: builder.query({
      query: () => createRequest(`/vibe/styles`),
      providesTags: ["styles"],
    }),
    deleteStyle: builder.mutation({
      query: (id) => createDeleteRequest(`/vibe/style/${id}`),
      invalidatesTags: ["styles"],
    }),
    createStyles: builder.mutation({
      query: (data) => createPostRequest(`/vibe/style`, data),
      invalidatesTags: ["styles"],
    }),
    updateStyles: builder.mutation({
      query: ({ id, data }) => createPostRequest(`/vibe/style/${id}`, data),
      invalidatesTags: ["styles"],
    }),
    getVibes: builder.query({
      query: (itemType) => createRequest(`/vibe`),
      providesTags: ["vibes"],
    }),
    deleteVibe: builder.mutation({
      query: (id) => createDeleteRequest(`/vibe/${id}`),
      invalidatesTags: ["vibes"],
    }),
    createVibe: builder.mutation({
      query: (data) => createPostRequest(`/vibe`, data),
      invalidatesTags: ["vibes"],
    }),
    updateVibe: builder.mutation({
      query: ({ id, data }) => createUpdateRequest(`/vibe/${id}`, data),
      invalidatesTags: ["vibes"],
    }),
    getAllConnections: builder.query({
      query: () => createRequest(`/connections/`),
      providesTags: ["connection"],
    }),
    createConnection: builder.mutation({
      query: (data) => createPostRequest(`/connections/`, data),
      invalidatesTags: ["connection"],
    }),
    deleteConnection: builder.mutation({
      query: (id) => createDeleteRequest(`/connections/${id}`),
      invalidatesTags: ["connection"],
    }),
    updateConnection: builder.mutation({
      query: ({ id, data }) => createUpdateRequest(`/connections/${id}`, data),
      invalidatesTags: ["connection"],
    }),
    getAllItemsAndLayouts: builder.query({
      query: () => createRequest(`/items/get-all`),
    }),
    getLayout: builder.mutation({
      query: (data) => createPostRequest(`/room/getroom-dev`, data),
    }),
    getAmazonList: builder.query({
      query: () => createRequest(`/vibe/amazonlistings/`),
    }),
    getIframes: builder.query({
      query: () => createRequest(`/amazon/`),
      providesTags: ["amazonList"],
    }),
    createIframe: builder.mutation({
      query: (data) => createPostRequest(`/amazon/`, data),
      invalidatesTags: ["amazonList"],
    }),
    deleteIframe: builder.mutation({
      query: (id) => createDeleteRequest(`/amazon/${id}`),
      invalidatesTags: ["amazonList"],
    }),
    updateIframe: builder.mutation({
      query: ({ id, data }) => createUpdateRequest(`/amazon/${id}`, data),
      invalidatesTags: ["amazonList"],
    }),
    getLogs: builder.query({
      query: ({
        page_size,
        activePage,
        statusFilter,
        zonesFilter,
        filterArea,
        prefrencesFilter,
        startDate,
        endDate,
      }) => {
        let url = `/log/?page=${activePage}&pageItems=${page_size}`;
        if (statusFilter?.value) {
          url += `&generated=${statusFilter?.value === 1 ? "true" : "false"}`;
        }

        if (zonesFilter?.value && prefrencesFilter?.value) {
          url += `&zone=${zonesFilter?.value},${prefrencesFilter?.value}`;
        } else if (zonesFilter?.value) {
          url += `&zone=${zonesFilter?.value}`;
        } else if (prefrencesFilter?.value) {
          url += `&zone=${prefrencesFilter?.value}`;
        }

        if (filterArea.length > 0) {
          url += `&area=${filterArea}`;
        }

        if (startDate) {
          url += `&startDate=${startDate?.format("YYYY-MM-DD")}`;
        }

        if (endDate) {
          url += `&endDate=${endDate?.format("YYYY-MM-DD")}`;
        }

        return createRequest(url);
      },
    }),
    singleLog: builder.query({
      query: ({ logId }) => createRequest(`/log/${logId}`),
    }),
    signup: builder.mutation({
      query: ({ body }) => createPostRequest(`/signup/`, body),
    }),
    partners: builder.query({
      query: (activePartner) => {
        if (activePartner) {
          return createRequest(`/partners?partner=${activePartner}`);
        } else {
          return createRequest(`/partners`);
        }
      },
      providesTags: ["partners"],
    }),
    deletePartner: builder.mutation({
      query: (id) => createDeleteRequest(`/partners/${id}`),
      invalidatesTags: ["partners"],
    }),
    addPartner: builder.mutation({
      query: (body) => createPostRequest(`/partners`, body),
      invalidatesTags: ["partners"],
    }),
    updatePartner: builder.mutation({
      query: ({ data, id }) => createUpdateRequest(`/partners/${id}`, data),
      invalidatesTags: ["partners"],
    }),
    products: builder.query({
      query: ({ partner, items, user, startDate, endDate }) => {
        let url = `/products?`;
        if (partner) url = url += `partner=${partner}&`;
        if (items) url = url += `items=${items}&`;
        if (user) url = url += `user=${user}&`;
        if (startDate) url += `startDate=${startDate?.format("YYYY-MM-DD")}&`;
        if (endDate) url += `endDate=${endDate?.format("YYYY-MM-DD")}&`;

        return createRequest(url);
      },
      providesTags: ["products"],
    }),
    deleteProduct: builder.mutation({
      query: (id) => createDeleteRequest(`/products/${id}`),
      invalidatesTags: ["products"],
    }),
    addProduct: builder.mutation({
      query: (body) => createPostRequest(`/products`, body),
      invalidatesTags: ["products"],
    }),
    updateProduct: builder.mutation({
      query: ({ data, id }) => createUpdateRequest(`/products/${id}`, data),
      invalidatesTags: ["products"],
    }),
    wishlist: builder.query({
      query: () => createRequest(`/wishlist`),
      providesTags: ["wishlist"],
    }),
    removeFromWishlist: builder.query({
      query: ({ wishlistId }) =>
        createRequest(`/wishlist/remove/${wishlistId}/`),
      invalidatesTags: ["wishlist"],
    }),
    addtoWishlist: builder.query({
      query: (id) => createRequest(`/wishlist/add/${id}/`),
      invalidatesTags: ["wishlist"],
    }),
    createSavedLayout: builder.mutation({
      query: (data) => createPostRequest(`/savedLayout/add/`, data),

      invalidatesTags: ["savedLayouts"],
    }),
    deleteSavedLayout: builder.mutation({
      query: (id) => createDeleteRequest(`/savedLayout/remove/${id}`),
      invalidatesTags: ["savedLayouts"],
    }),
    getSavedLayoutSpecficUser: builder.query({
      query: () => createRequest(`/savedLayout/`),
      providesTags: ["savedLayouts"],
    }),
    getSavedLayoutByPartnerId: builder.query({
      query: ({ id, page, user, startDate, endDate }) => {
        const partnerId = id || null; 
        let url = `/savedLayout/getByPartner/${partnerId}?page=${page}`;
    
        if (user) url += `&userId=${user}`;
    
        if (startDate && endDate) {
          url += `&startDate=${startDate?.format("YYYY-MM-DD")}`;
          url += `&endDate=${endDate?.format("YYYY-MM-DD")}`;
        }
    
        return createRequest(url);
      },
      providesTags: ["savedLayouts"],
      keepUnusedDataFor: 0,
    }),
    getSavedLayoutById: builder.query({
      query: ({ id, version }) => {
        let url = `/savedLayout/getByIdentifier/${id}`;
        if (version) url += `?version=${version}`;

        return createRequest(url);
      },
      providesTags: ["savedLayouts"],
      keepUnusedDataFor: 300, // 5 min cache - prevents loading flash on editor re-entry
    }),
    getLayoutVersionsByID: builder.query({
      query: (id) => createRequest(`/savedLayout/getLayoutVersionsByID/${id}`),
      providesTags: (_, ___, id) => [{ type: "savedLayouts", id }],
      keepUnusedDataFor: 240,
    }),
    getLayoutsEngagementByUser: builder.query({
      query: ({ userId, startDate, endDate, layout }) => {
        let url = `/savedLayout/engagements/${userId}?`;

        if (startDate) url += `startDate=${startDate?.format("YYYY-MM-DD")}&`;

        if (endDate) url += `endDate=${endDate?.format("YYYY-MM-DD")}&`;

        if (layout) url += `layout=${layout}`;

        return createRequest(url);
      },
      providesTags: ["savedLayouts"],
    }),
    getLayoutsEngagementByPartner: builder.query({
      query: ({ partnerId, startDate, endDate, user }) => {
        // Aggregate across partners when partnerId is missing, empty, or the literal "all"
        const segment =
          partnerId === "all" || partnerId === "" || partnerId == null
            ? "all"
            : partnerId;
        let url = `/savedLayout/partner-engagements/${segment}?`;

        if (startDate) url += `startDate=${startDate?.format("YYYY-MM-DD")}&`;

        if (endDate) url += `endDate=${endDate?.format("YYYY-MM-DD")}&`;

        if (user) url += `user=${user}&`;

        return createRequest(url);
      },
      providesTags: ["savedLayouts"],
      keepUnusedDataFor: 0
    }),
    getEngagementLogs: builder.query({
      query: ({ userId, startDate, endDate, partnerId, downloadsPage, viewsPage, sharesPage, layoutId, timezone }) => {
        let url = `/savedLayout/engagement-logs?`;

        if (timezone) url += `timezone=${timezone}&`;

        const formatDateParam = (d) => {
          if (d == null || d === "") return null;
          if (typeof d === "string") {
            return d.length >= 10 ? d.slice(0, 10) : d;
          }
          if (typeof d.format === "function") {
            return d.format("YYYY-MM-DD");
          }
          return null;
        };

        const startStr = formatDateParam(startDate);
        const endStr = formatDateParam(endDate);
        if (startStr) url += `startDate=${encodeURIComponent(startStr)}&`;
        if (endStr) url += `endDate=${encodeURIComponent(endStr)}&`;

        if (partnerId) url += `partnerId=${partnerId}&`;

        if (downloadsPage) url += `downloadsPage=${downloadsPage}&`;

        if (viewsPage) url += `viewsPage=${viewsPage}&`;

        if (sharesPage) url += `sharesPage=${sharesPage}&`;

        if (userId) url += `user=${userId}&`;

        if (layoutId) url += `layoutId=${layoutId}`;

        return createRequest(url);
      },
      providesTags: ["savedLayouts"],
      keepUnusedDataFor: 0
    }),
    getAllSavedLayoutsForAdmin: builder.query({
      query: (arg) => {
        // Legacy: string userId or undefined → /savedLayout/all[?userId=]
        if (arg == null || typeof arg === "string") {
          let url = `/savedLayout/all`;
          if (arg) url += `?userId=${arg}`;
          return createRequest(url);
        }
        // Paginated / filtered admin list (all partners)
        const { page = 1, user, startDate, endDate } = arg;
        let url = `/savedLayout/all?page=${page}`;
        if (user) url += `&userId=${user}`;
        if (startDate && endDate) {
          url += `&startDate=${startDate.format("YYYY-MM-DD")}&endDate=${endDate.format("YYYY-MM-DD")}`;
        }
        return createRequest(url);
      },
      providesTags: ["savedLayouts"],
    }),
    dologin: builder.mutation({
      query: ({ body }) => createPostRequest(`/user/login`, body),
      invalidatesTags: ["savedLayouts", "wishlist"],
    }),
    getProfile: builder.query({
      query: () => createRequest(`user/getLoggedInUser`),
      providesTags: ["UserProfile"],
    }),
    changePsw: builder.mutation({
      query: ({ body }) => createPostRequest(`user/changePassword/`, body),
    }),
    requestNewPassword: builder.mutation({
      query: ({ body }) => createPostRequest(`user/requestNewPassword/`, body),
    }),
    resetPassword: builder.mutation({
      query: ({ body }) => createPostRequest(`user/resetPassword/`, body),
    }),
    deleteVersionFromSavedLayout: builder.mutation({
      query: ({ layoutId, versionId }) =>
        createDeleteRequest(
          `savedLayout/remove-version/${layoutId}/${versionId}`
        ),
      invalidatesTags: ["amazonList"],
    }),
    trackEngagement: builder.mutation({
      query: ({ layoutId, versionId, body }) => createPostRequest(`/savedLayout/trackEngagement/${layoutId}/${versionId}`, body),
    }),
    getPackages: builder.query({
      query: () => createRequest(`/partnersPackage`),
      providesTags: ["packages"],
    }),
    createPackage: builder.mutation({
      query: (data) => createPostRequest(`/partnersPackage`, data),
      invalidatesTags: ["packages"],
    }),
    updatePackage: builder.mutation({
      query: ({ id, data }) =>
        createUpdateRequest(`/partnersPackage/${id}`, data),
      invalidatesTags: ["packages"],
    }),
    deletePackage: builder.mutation({
      query: (packageId) =>
        createDeleteRequest(`/partnersPackage/${packageId}`),
      invalidatesTags: ["packages"],
    }),
    getPartnerZones: builder.query({
      query: (partnerId) => createRequest(`/partnersZone?partner=${partnerId}`),
      providesTags: ["partnerZones"],
    }),
    createPartnerZones: builder.mutation({
      query: ({ data  , id}) => createPostRequest(`/partnersZone/bulk?partner=${id}`, data),
      invalidatesTags: ["partnerZones"],
    }),

    // -----------------------------------------------------------------------
    // TODO (Backend): Implement GET /external-upload/activity
    //
    // Returns a paginated list of external AI upload sessions.
    //
    // Query params:
    //   env   – "staging" | "production"
    //   page  – 1-based page number
    //   limit – rows per page (default 20)
    //
    // Response shape:
    // {
    //   items: Array<{
    //     session_id:         string,
    //     created_at:         string,          // ISO 8601
    //     source:             string | null,   // referrer / parent URL, or null
    //     uploaded_file_url:  string | null,   // link to uploaded floor plan image
    //     time_spent_seconds: number | null    // total session duration in seconds
    //   }>,
    //   page:  number,
    //   limit: number,
    //   total: number,
    // }
    // -----------------------------------------------------------------------
    getExternalUploadActivity: builder.query({
      query: ({ env = "staging", page = 1, limit = 20 }) => {
        let url = `/external-upload/activity?env=${env}&page=${page}&limit=${limit}`;
        return createRequest(url);
      },
      providesTags: ["externalUploadActivity"],
      keepUnusedDataFor: 0,
    }),
    getAdminApiKeys: builder.query({
      query: ({ includeInactive = false } = {}) =>
        createRequest(adminApiKeyListPath(includeInactive)),
      transformResponse: (response) => normalizeAdminApiKeyList(response),
      providesTags: ["apiKeys"],
    }),
    createAdminApiKey: builder.mutation({
      query: (data) => createPostRequest(`/apikeys/admin/generate`, data),
      invalidatesTags: ["apiKeys"],
    }),
    revokeAdminApiKey: builder.mutation({
      query: (keyId) => createDeleteRequest(`/apikeys/admin/${encodeURIComponent(keyId)}`),
      invalidatesTags: ["apiKeys"],
    }),
    submitGalleryInquiry: builder.mutation({
      query: (body) => createPostRequest(`/gallery-inquiries`, body),
    }),
    setGalleryInquiryContact: builder.mutation({
      query: (body) => createPostRequest(`/gallery-inquiries/contact`, body),
    }),
  }),
});

export const {
  useGetAllUsersQuery,
  useDeleteUserMutation,
  useCreateUserMutation,
  useEditUserMutation,
  useCreateItemsMutation,
  useGetAllItemsQuery,
  useDeleteItemMutation,
  useUpdateItemsMutation,
  useCreateConnectionMutation,
  useGetAllConnectionsQuery,
  useDeleteConnectionMutation,
  useUpdateConnectionMutation,
  useGetAllItemsAndLayoutsQuery,
  useGetLayoutMutation,
  useGetStylesQuery,
  useCreateStylesMutation,
  useDeleteStyleMutation,
  useUpdateStylesMutation,
  useGetVibesQuery,
  useDeleteVibeMutation,
  useCreateVibeMutation,
  useUpdateVibeMutation,
  useGetAmazonListQuery,
  useCreateIframeMutation,
  useDeleteIframeMutation,
  useGetIframesQuery,
  useUpdateIframeMutation,
  useGetLogsQuery,
  useSingleLogQuery,
  useSignupMutation,
  usePartnersQuery,
  useDeletePartnerMutation,
  useAddPartnerMutation,
  useUpdatePartnerMutation,
  useProductsQuery,
  useDeleteProductMutation,
  useAddProductMutation,
  useUpdateProductMutation,
  useWishlistQuery,
  useAddtoWishlistQuery,
  useCreateSavedLayoutMutation,
  useGetSavedLayoutSpecficUserQuery,
  useGetSavedLayoutByIdQuery,
  useGetLayoutVersionsByIDQuery,
  useGetSavedLayoutByPartnerIdQuery,
  useGetLayoutsEngagementByUserQuery,
  useGetLayoutsEngagementByPartnerQuery,
  useGetEngagementLogsQuery,
  useGetAllSavedLayoutsForAdminQuery,
  useTrackEngagementMutation,
  useDologinMutation,
  useDeleteSavedLayoutMutation,
  useRemoveFromWishlistQuery,
  useGetProfileQuery,
  useChangePswMutation,
  useRequestNewPasswordMutation,
  useResetPasswordMutation,
  useDeleteVersionFromSavedLayoutMutation,
  useGetPackagesQuery,
  useCreatePackageMutation,
  useUpdatePackageMutation,
  useDeletePackageMutation,
  useGetPartnerZonesQuery,
  useCreatePartnerZonesMutation,
  useGetExternalUploadActivityQuery,
  useGetAdminApiKeysQuery,
  useCreateAdminApiKeyMutation,
  useRevokeAdminApiKeyMutation,
  useSubmitGalleryInquiryMutation,
  useSetGalleryInquiryContactMutation,
} = apiRoomNate;
